import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ContainerVm, ProgramVm, ResourceVm } from 'qp-ui-interface';
import { DataService } from '../../../data.service';
import { EntitlementService } from '../../entitlements/entitlement.service';
import { LocalStorageKeys } from '../../LocalStorageKeys';
import { openBlockedContentMessageDialog } from '../../shared-components/shared-dialogs';
import { isLoggedIn } from '../../utils/LoginUtils';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { NavigationLibraryService } from '../navigation/navigation.service';
import { ParentalControlDialogService } from '../parental-pin/parental-pin-dialog.service';
import { PlaybackService } from '../playback/playback.service';
import { getContentStartedInit } from '../../utils/EventUtlis';

@Injectable()
export class RoutingService {
  constructor(
    private readonly router: Router,
    private readonly dataService: DataService,
    private readonly navService: NavigationLibraryService,
    private readonly playbackService: PlaybackService,
    private readonly location: Location,
    private readonly parentalControlDialogService: ParentalControlDialogService,
    private readonly localStorage: LocalStorageService,
    private readonly entitlementService: EntitlementService,
    private readonly dialog: MatDialog
  ) {}

  async navigateToPlayerWithValidations(
    resource: ResourceVm,
    isContinueWatching: boolean,
    source?: string,
    keepNavigationPos = false
  ): Promise<boolean> {
    try {
      getContentStartedInit(this.localStorage);
      const isUserEntitled = await this.entitlementService.isUserEntitledResource(resource);
      if (isUserEntitled) {
        const allowAccess = await this.parentalControlDialogService.checkExplicitContent(resource);
        if (allowAccess) {
          this.dataService.isContinueWatching = isContinueWatching;
          return this.navigateDirectoToPlayer(resource);
        }
        const focusPosition = { verticalIndex: this.navService.getVerticalIndex(), horizontalIndex: this.navService.getHorizontalIndex() };
        if (keepNavigationPos) {
          this.navService.setVerticalIndex(focusPosition.verticalIndex);
          this.navService.setHorizontalIndex(focusPosition.horizontalIndex);
        }
      } else {
        const isLogged = isLoggedIn(this.localStorage);
        openBlockedContentMessageDialog(this.dialog, isLogged).then((value) => {
          if (value) {
            if (!isLogged) {
              const { type, id: contentID } = resource;
              source = `player/${type}/${contentID}`;
              this.navigateToLogin(source);
            }
          }
        });
      }
      return Promise.resolve(false);
    } catch (e) {
      console.log('RoutingService.navigateToPlayer() error', e);
      return Promise.resolve(false);
    }
  }

  async navigateDirectoToPlayerFromChannelWithValidations(resource: ProgramVm, source?: string): Promise<boolean> {
    try {
      const isUserEntitled = await this.entitlementService.isUserEntitledProgram(resource);
      if (isUserEntitled) {
        const allowAccess = await this.parentalControlDialogService.checkExplicitProgramContent(resource);
        if (allowAccess) {
          return this.navigateDirectoToPlayerFromChannel(resource);
        }
      } else {
        const isLogged = isLoggedIn(this.localStorage);
        openBlockedContentMessageDialog(this.dialog, isLogged).then((value) => {
          if (value) {
            if (!isLogged) {
              this.navigateToLogin(source);
            }
          }
        });
      }
      return Promise.resolve(false);
    } catch (e) {
      console.log('RoutingService.navigateDirectoToPlayerFromChannel() error', e);
      return Promise.resolve(false);
    }
  }

  async navigateDirectoToPlayerFromCatchupWithValidations(resource: ProgramVm, source?: string): Promise<boolean> {
    try {
      const isUserEntitled = await this.entitlementService.isUserEntitledProgram(resource);
      if (isUserEntitled) {
        const allowAccess = await this.parentalControlDialogService.checkExplicitProgramContent(resource);
        if (allowAccess) {
          const startTime = new Date(resource.startTimeEpoch).toISOString();
          const endTime = new Date(resource.endTimeEpoch).toISOString();
          const type = 'channel';
          const contentID = resource.channelId;
          return this.router.navigate([`player/${type}/${contentID}/catchup/${startTime}/${endTime}`], {
            state: { type, id: contentID, resource },
          });
        }
      } else {
        const isLogged = isLoggedIn(this.localStorage);
        openBlockedContentMessageDialog(this.dialog, isLogged).then((value) => {
          if (value) {
            if (!isLogged) {
              this.navigateToLogin(source);
            }
          }
        });
      }
      return Promise.resolve(false);
    } catch (e) {
      console.log('RoutingService.navigateDirectoToPlayerFromCatchup() error', e);
      return Promise.resolve(false);
    }
  }

  async navigateDirectoToPlayer(resource: ResourceVm): Promise<boolean> {
    const { type, id: contentID } = resource;
    // set actual time to start time playback service
    this.setStartTime();
    return this.router.navigate([`player/${type}/${contentID}`], { state: { type, id: contentID, resource } });
  }

  async navigateDirectoToPlayerFromChannel(resource: ProgramVm): Promise<boolean> {
    const { channelId: contentID } = resource;
    const type = 'channel';
    // set actual time to start time playback service
    this.setStartTime();
    const focusPosition = { verticalIndex: this.navService.getVerticalIndex(), horizontalIndex: this.navService.getHorizontalIndex() };
    if (focusPosition.verticalIndex && focusPosition.horizontalIndex) {
      this.navService.setVerticalIndex(focusPosition.verticalIndex);
      this.navService.setHorizontalIndex(focusPosition.horizontalIndex);
    }
    return this.router.navigate([`player/${type}/${contentID}`], { state: { type, id: contentID, resource } });
  }

  async navigateToDetails(resource: ResourceVm, container?: ContainerVm): Promise<boolean> {
    const { type, id: contentID, collectionID, collectionName, uniqueId, contentType } = resource;
    if (contentType && uniqueId) {
      return this.router.navigate([`/${type}/${contentID}`], {
        state: { type, id: contentID, resource, container, collectionID, collectionName },
      });
    }
    return false;
  }

  navigateToLogin(source: string): void {
    this.router.navigate(['/link-device'], { queryParams: { source } });
  }

  navigateToPin(source: string, deeplink: boolean): Promise<boolean> {
    return this.router.navigate(['/on-boarding/pin'], {
      state: JSON.parse(this.localStorage.getItem(LocalStorageKeys.PROFILES)),
      queryParams: { source, skip: true, deeplink },
    });
  }

  async navigatePlayerUrl(backUrl: string) {
    if (backUrl === 'LOCATION_BACK') {
      this.location.back();
    } else {
      await this.router.navigateByUrl(backUrl);
    }
  }

  setStartTime(): void {
    this.playbackService.startTime = new Date().getTime();
    this.dataService.playbackStartTime = new Date().getTime();
  }
}
